/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Modal,
  DatePicker,
  Spin,
  Divider,
} from 'antd';
import { DATE_FORMAT } from 'utils/formatters';
import { patchGenerarDeclaracion, postDeclaracionCedularProfesionalAnual } from 'api/recaudacion';
import { SyncOutlined } from '@ant-design/icons';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import _logger from 'utils/logger';
import propTypes from 'prop-types';
import moment from 'moment';
import { RFC_PATTERN, CURP_PATTERN } from 'utils/patterns';
import { toMoment } from 'utils/normalizers';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC valido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP valida.' }],
};

const FormCedularMensual = ({
  padron,
  statusDeclaracion,
  visible,
  setVisibleForm,
  isComplementaria,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  // Resetea el formulario cuando se cierra.
  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible]);

  function redondear(numero) {
    const decimal = numero - Math.floor(numero);

    if (decimal >= 0.51) {
      return Math.ceil(numero);
    }
    return Math.floor(numero);
  }

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }
      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo: padron?.empresa?.contribuyente?.razon_social || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        entidad: 1,
        fecha_del_pago_realizado_anterior: isComplementaria && !isZeroState && !disableFields ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          impuesto_anual_determinado: 0,
          total_de_ingresos_obtenidos_anuales: 0,
          impuesto_cedular_anual_a_cargo: 0,
          retenciones_por_terceros: 0,
          base_del_pago: 0,
          deducciones_autorizadas_comprobables_anuales: 0,
          monto_pagado_con_anterioridad: 0,
          actualizacion: 0,
          multa_por_correccion_fiscal: 0,
          recargos: 0,
          retenciones: 0,
          total_de_contribuciones: 0,
          pagos_provisionales_efectuados: 0,
          impuesto_cedular_a_cargo: 0,
          saldo_a_favor: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionCedularProfesionalAnual(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validacionesDeOperaciones = (values) => {
    const retencion_rounded = parseInt(redondear(values.retenciones), 10);
    const impuesto_rounded = parseInt(redondear(values.impuesto), 10);
    const impuesto_cedular_anual_a_cargo = parseInt(values.impuesto_cedular_anual_a_cargo, 10);
    const saldo_a_favor = parseInt(values.saldo_a_favor, 10);

    // ImpuestosCorrespondiente menos Retenciones = ImpuestoCargo(debe ser mayor igual a cero)
    if ((parseFloat(impuesto_rounded) - parseFloat((retencion_rounded))) < 0 && !isZeroState) {
      form.validateFields(['impuesto_a_cargo']).then(() => {
        form.setFields([
          {
            name: 'impuesto_a_cargo',
            errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
          },
        ]);
      });
      form.validateFields(['retenciones']).then(() => {
        form.setFields([
          {
            name: 'retenciones',
            errors: ['El valor no puede ser mayor al impuesto correspondiente.'],
          },
        ]);
      });
    } else if ((parseFloat(impuesto_rounded) - parseFloat((retencion_rounded))) >= 0 && !isZeroState) {
      form.validateFields(['impuesto_a_cargo']).then(() => {
        form.setFields([
          {
            name: 'impuesto_a_cargo',
            errors: [],
          },
        ]);
      });
      form.validateFields(['retenciones']).then(() => {
        form.setFields([
          {
            name: 'retenciones',
            errors: [],
          },
        ]);
      });
    }

    // Dedduciones autorizadas no debe ser mayor a total_de_ingresos
    if (parseInt(saldo_a_favor, 10) > 0) {
      form.validateFields(['impuesto_cedular_anual_a_cargo']).then(() => {
        // setDisableFields(true);
        form.setFieldsValue({
          actualizacion: 0,
          recargos: 0,
          multa_por_correccion_fiscal: 0,
          monto_pagado_con_anterioridad: 0,
          total_de_contribuciones: 0,
          fecha_del_pago_realizado_anterior: null,
          cantidad_a_cargo: 0,
        });
      });
    } else {
      form.validateFields(['impuesto_cedular_anual_a_cargo']).then(() => {
        setDisableFields(false);
      });
    }

    if (parseInt(impuesto_cedular_anual_a_cargo, 10) === 0) {
      form.setFieldsValue({ cantidad_a_cargo: parseFloat(impuesto_cedular_anual_a_cargo).toFixed(2) });
    }

    if ((parseFloat(impuesto_rounded) - parseFloat((retencion_rounded))) <= 0 && !isZeroState) {
      form.setFieldsValue({ actualizacion: 0, recargos: 0, multa_por_correccion_fiscal: 0 });
    }
  };

  // Calcula el total de la cantidad a pagar.
  const total = () => {
    const values = form.getFieldsValue();

    const actualizacion = parseFloat(values.actualizacion) || 0;
    const recargos = parseFloat(values.recargos) || 0;
    const multas = parseFloat(values.multa_por_correccion_fiscal) || 0;
    const monto_pagado_con_anterioridad = parseFloat(values.monto_pagado_con_anterioridad) || 0;

    const impuesto1 = parseInt(values.total_de_ingresos_obtenidos_anuales, 10) || 0;
    const impuesto2 = parseInt(values.deducciones_autorizadas_comprobables_anuales, 10) || 0;

    const impuesto3 = parseInt(values.retenciones_por_terceros, 10) || 0;
    const impuesto4 = parseInt(values.pagos_provisionales_efectuados, 10) || 0;

    const base_del_pago_total = [
      impuesto2,
      impuesto1,
    ].reduce((a, b) => b - a, 0);

    const total_de_contribuciones = [
      actualizacion,
      recargos,
      multas,
    ].reduce((a, b) => parseFloat(a) + parseFloat(b), 0).toFixed(2);

    const base_de_pago = base_del_pago_total < 0 ? 0 : redondear(parseFloat(base_del_pago_total).toFixed(2));
    const impuesto_anual_determinado = redondear(parseFloat(base_de_pago).toFixed(2) * 0.03, 10);

    const impuesto_cedular = [
      impuesto_anual_determinado,
      impuesto3,
      impuesto4,
    ].reduce((a, b) => a - b);

    const impuesto_cedular_a_cargo_positive = impuesto_cedular < 0 ? 0 : impuesto_cedular;

    const saldo_a_favor = (parseInt(impuesto3, 10) + parseInt(impuesto4, 10)) - parseInt(impuesto_anual_determinado, 10);
    const saldo_a_favor_rounded = saldo_a_favor < 0 ? 0 : saldo_a_favor;

    setDisableFields(saldo_a_favor_rounded > 0);

    const total_de_contribuciones_rounded = redondear(parseFloat(
      parseInt(impuesto_cedular_a_cargo_positive, 10)
      + parseInt(total_de_contribuciones, 10),
    ).toFixed(2));

    const cantidad_a_cargo = parseFloat(total_de_contribuciones_rounded - monto_pagado_con_anterioridad).toFixed(2);
    const cantidad_a_pagar_rounded = redondear(parseFloat(cantidad_a_cargo).toFixed(2));

    form.setFieldsValue({
      base_del_pago: parseFloat(base_de_pago).toFixed(2),
      impuesto_anual_determinado,
      total_de_contribuciones: total_de_contribuciones_rounded,
      impuesto_cedular_anual_a_cargo: redondear(parseFloat(impuesto_cedular_a_cargo_positive).toFixed(2)),
      impuesto_cedular_a_cargo: redondear(parseFloat(impuesto_cedular_a_cargo_positive).toFixed(2)),
      saldo_a_favor: saldo_a_favor_rounded,
    });

    const isNegative = parseInt(total_de_contribuciones_rounded, 10) < parseInt(monto_pagado_con_anterioridad, 10);

    if (isNegative) {
      const total_a_favor_rounded = redondear(parseFloat((monto_pagado_con_anterioridad - total_de_contribuciones_rounded)).toFixed(2));
      form.setFieldsValue({
        cantidad_a_favor: parseFloat(total_a_favor_rounded).toFixed(2),
        cantidad_a_cargo: parseFloat(0).toFixed(2),
      });
    } else {
      form.setFieldsValue({
        cantidad_a_cargo: parseFloat(cantidad_a_pagar_rounded).toFixed(2),
        cantidad_a_favor: parseFloat(0).toFixed(2),
      });
    }
    validacionesDeOperaciones({
      ...values,
      impuesto_cedular_anual_a_cargo: redondear(parseFloat(impuesto_cedular_a_cargo_positive).toFixed(2)),
      impuesto_cedular_a_cargo: redondear(parseFloat(impuesto_cedular_a_cargo_positive).toFixed(2)),
      saldo_a_favor: saldo_a_favor_rounded,
    });
  };

  // useEffect(() => {
  //   calcularActualizacion({ importe: _impuesto });
  //   calcularRecargos({ importe: _impuesto });
  //   setTimeout(() => total(), 1000);
  // }, [_impuesto]);

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              PAGO PROVISIONAL ANUAL DE IMPUESTO CEDULAR POR LA PRESTACIÓN DE SERVICIOS PROFESIONALES
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
              >
                Cancelar
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                Generar
              </Button>
            </Col>
          </Row>
      )}
        width="40vw"
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{
              fecha_del_pago_realizado_anterior: moment(),
              es_declaracion_en_ceros: false,
              total_de_ingresos_obtenidos_anuales: 0,
              deducciones_autorizadas_comprobables_anuales: 0,
              retenciones_por_terceros: 0,
              impuesto_cedular_anual_a_cargo: 0,
              pagos_provisionales_efectuados: 0,
              base_del_pago: 0,
              recargos: 0,
              multa_por_correccion_fiscal: 0,
              actualizacion: 0,
              cantidad_a_cargo: 0,
              cantidad_a_favor: 0,
              total_de_contribuciones: 0,
              impuesto_anual_determinado: 0,
              monto_pagado_con_anterioridad: isComplementaria ? 0 : 0,
              saldo_a_favor: 0,
              impuesto_cedular_a_cargo: 0,
            }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={10}>
              { isZeroState ? (
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24} sm={24} md={24}>
                  <Text $accent $bold>II. DETERMINACIÓN DEL IMPUESTO </Text>
                  <table style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>Concepto</th>
                        <th>Monto</th>
                      </tr>
                      <tr>
                        <td>Total de ingresos obtenidos en el año</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            tooltip="prueba"
                            name="total_de_ingresos_obtenidos_anuales"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" onChange={() => total()} />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Deducciones autorizadas</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="deducciones_autorizadas_comprobables_anuales"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" onChange={() => total()} />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Base del pago</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="base_del_pago"
                            rules={
                              [_rules.required('El campo es requerido'),
                                ..._rules.numeric,
                              ]
                            }
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Impuesto anual determinado</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto_anual_determinado"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Pagos provisionales efectuados</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="pagos_provisionales_efectuados"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" onChange={() => total()} />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Retenciones por teceros</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="retenciones_por_terceros"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" onChange={() => total()} />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Impuesto cedular anual a cargo</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto_cedular_anual_a_cargo"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Saldo a favor</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="saldo_a_favor"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <Divider />
                      <Text $accent $bold>III. PAGO DEL IMPUESTO</Text>
                      <tr>
                        <td>Impuesto cedular a cargo</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="impuesto_cedular_a_cargo"
                            rules={[
                              _rules.required('El campo es requerido'),
                              ..._rules.entero,
                              ..._rules.numeric,
                            ]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Actualización (Monto manual)</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="actualizacion"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input
                              prefix="$"
                              onChange={() => total()}
                              disabled={disableFields}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Recargos (Monto manual)</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="recargos"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input
                              prefix="$"
                              onChange={() => total()}
                              disabled={disableFields}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Multas (Monto manual)</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="multa_por_correccion_fiscal"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input
                              prefix="$"
                              onChange={() => total()}
                              disabled={disableFields}
                            />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Total de contribuciones</td>
                        <td>
                          <Form.Item
                            hasFeedback
                            name="total_de_contribuciones"
                            rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      {
                        isComplementaria && (
                          <>
                            <tr>
                              <td>Fecha del pago anterior</td>
                              <td>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  // rules={[_rules.required('El campo es requerido')]}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabled={disableFields}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td>Monto pagado con anterioridad (Monto manual)</td>
                              <td>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El campo es requerido'),
                                    ..._rules.numeric,
                                  ]}
                                >
                                  <Input
                                    prefix="$"
                                    onChange={() => { total(); }}
                                    disabled={disableFields}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )
                      }
                      <tr>
                        <td>Cantidad a pagar</td>
                        <td>
                          <Form.Item
                            name="cantidad_a_cargo"
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                      <tr>
                        <td>Cantidad a favor</td>
                        <td>
                          <Form.Item
                            name="cantidad_a_favor"
                          >
                            <Input prefix="$" disabled />
                          </Form.Item>
                        </td>
                      </tr>
                    </thead>
                  </table>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormCedularMensual.propTypes = {
  padron: propTypes.shape({
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  callbackCancel: propTypes.func,
  declaracionId: propTypes.func.isRequired,
};

FormCedularMensual.defaultProps = {
  isComplementaria: false,
  visible: false,
  callbackCancel: () => {},
};

export default FormCedularMensual;
